import { createContext, useEffect, useReducer } from "react";
import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
// import logger from 'use-reducer-logger';

import axios from "src/lib/axios";
import axiosNoAuth from "src/lib/axiosNoAuth";
// import { encryptJson } from "src/lib/encrypt";
import { IAuthenticate, IUser } from "src/models/account/accountModel";
import { getMenus } from "src/redux/account/connectionServices";

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: IUser | null;
  userMenus: null;
}

interface AuthContextValue extends State {
  platform: "JWT";
  login: (email: string, password: string, otp: number) => Promise<void>;
  updateUser: (data: any) => Promise<void>;
  updateMenu: (userMenus: any) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (
    userEmail: string,
    oldPassword: string,
    password: string
  ) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: "INITIALIZE";
  payload: {
    isAuthenticated: boolean;
    user: IUser | null;
    userMenus: null;
  };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: IUser;
    userMenus: null;
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type LogoutNavAction = {
  type: "LOGOUT_NAV";
};

type UpdateUserAction = {
  type: "UPDATE_USER";
  payload: {
    data: any;
  };
};
type UpdateUserMenuAction = {
  type: "UPDATE_USERMENU";
  payload: {
    data: any;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutNavAction
  | LogoutAction
  | UpdateUserAction
  | UpdateUserMenuAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  userMenus: null,
};

const setSession = (accessToken: string | null): void => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "INITIALIZE":
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        userMenus: action.payload.userMenus,
      };

    case "LOGIN":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        userMenus: action.payload.userMenus,
      };

    case "LOGOUT_NAV":
      return {
        ...state,
        isAuthenticated: false,
      };

    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        userMenus: null,
      };

    case "UPDATE_USER":
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload.data,
        },
      };

    case "UPDATE_USERMENU":
      return {
        ...state,
        userMenus: action.payload.data,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: "JWT",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  updateUser: () => Promise.resolve(),
  updateMenu: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        const storedUserDetails: string | null =
          window.localStorage.getItem("userDetails");
        const storedMenuDetails: string | null =
          window.localStorage.getItem("menuDetails");

        // console.log({accessToken, storedUserDetails, storedMenuDetails});

        if (accessToken && storedUserDetails && storedMenuDetails) {
          const userDetails = JSON.parse(storedUserDetails);
          const menuDetails = JSON.parse(storedMenuDetails);
          setSession(accessToken);

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: {
                ...userDetails,
              },
              userMenus: menuDetails,
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
              userMenus: null,
            },
          });
        }
      } catch (err) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
            userMenus: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const updateUser = async (data: any): Promise<any> => {
    dispatch({
      type: "UPDATE_USER",
      payload: { data },
    });
  };

  const updateMenu = async (data: any): Promise<any> => {
    dispatch({
      type: "UPDATE_USERMENU",
      payload: { data },
    });
  };

  const login = async (userName: string, password: string): Promise<any> => {
    const response = await axiosNoAuth
      .post<IAuthenticate>("/ConfigAccount/ConfigAuthenticate", {
        username: userName,
        password,
      })
      .catch((err) => {
        throw err;
      });

    const { token } = response.data;
    if (token) {
      setSession(token);
      const userResponse = await axios
        .get<any>(`/ConfigAccount/GetUserByUserName/${userName}`)
        .catch((err) => {});
      const { appKey } = response.data;
      const {
        username,
        configUserKey,
        configUserGroup,
        configUserGroupKey,
        fullName,
        email
      } = userResponse && userResponse.data;
      const menus = await getMenus(configUserKey);
      const userDetails = {
        username,
        configUserKey,
        configUserGroupKey,
        configUserGroup,
        fullName,
        email,
        appKey
      };
      window.localStorage.setItem("userDetails", JSON.stringify(userDetails));
      window.localStorage.setItem("menuDetails", JSON.stringify(menus));

      dispatch({
        type: "LOGIN",
        payload: {
          user: userDetails,
          userMenus: menus,
        },
      });
      return response.data;
    }
    return response;
  };

  const logout = async (): Promise<void> => {
    window.localStorage.removeItem("userDetails");
    dispatch({ type: "LOGOUT_NAV" });
    dispatch({ type: "LOGOUT" });
    setSession(null);
  };

  const resetPassword = async (data: any): Promise<any> => {
    const response = await axios
      .post("/ConfigUser/ResetPassword", {
        userEmail: data.userEmail,
        newPassword: data.newPassword,
        oldPassword: data.oldPassword,
      })
      .catch((err) => {
        throw err;
      });
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "JWT",
        login,
        logout,
        updateUser,
        updateMenu,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
